import React, { memo, useCallback } from 'react';

import PropTypes from 'prop-types';

import Pricing from '../../Pricing';
import { aggregatedPricingFieldsPropType } from '../helpers/aggregatedFieldPropTypes';
import { useBindAggregatedField } from '../hooks/useBindAggregatedField';
import {
  getAggregatedCommonBoolean,
  getAggregatedCommonString,
} from '../helpers/aggregatedFieldHelpers';

const AggregatedPricingSection = ({
  pricingFields,
  readOnly,
  readOnlyTooltip,
  detailsPanelVisibility,
  toggleVisibility,
  blockName,
  blockTitle,
}) => {
  const priceBindings = useBindAggregatedField('price', pricingFields?.price, 'Price');
  const compareBindings = useBindAggregatedField(
    'compareAtPrice',
    pricingFields?.compareAtPrice,
    'Compare-at price',
  );
  const taxableBindings = useBindAggregatedField(
    'taxable',
    pricingFields?.taxable,
    'Charge tax on this product',
  );
  const costBindings = useBindAggregatedField('cost', pricingFields?.cost, 'Cost per item');

  const onChangePrice = useCallback((price) => {
    priceBindings.onChange(String(price));
  }, [priceBindings]);

  const onChangeCompareAtPrice = useCallback((compareAtPrice) => {
    compareBindings.onChange(
      compareAtPrice === '' || compareAtPrice == null ? '' : String(compareAtPrice),
    );
  }, [compareBindings]);

  const onChangeTaxable = useCallback((taxable) => {
    taxableBindings.onChange(taxable);
  }, [taxableBindings]);

  const onChangeUnitCost = useCallback((amount) => {
    costBindings.onChange(amount === '' || amount == null ? '' : String(amount));
  }, [costBindings]);

  if (!pricingFields) return null;

  const costAmount = getAggregatedCommonString(pricingFields.cost, '');

  return (
    <Pricing
      aggregatedFields={pricingFields}
      price={getAggregatedCommonString(pricingFields.price, '')}
      compareAtPrice={getAggregatedCommonString(pricingFields.compareAtPrice, '')}
      taxable={getAggregatedCommonBoolean(pricingFields.taxable, false)}
      unitCost={costAmount === '' ? null : { amount: costAmount }}
      readOnly={readOnly}
      readOnlyTooltip={readOnlyTooltip}
      detailsPanelVisibility={detailsPanelVisibility}
      toggleVisibility={toggleVisibility}
      blockName={blockName}
      blockTitle={blockTitle || 'Pricing'}
      onChangePrice={onChangePrice}
      onChangeCompareAtPrice={onChangeCompareAtPrice}
      onChangeTaxable={onChangeTaxable}
      onChangeUnitCost={onChangeUnitCost}
    />
  );
};

AggregatedPricingSection.propTypes = {
  pricingFields: aggregatedPricingFieldsPropType,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  detailsPanelVisibility: PropTypes.objectOf(PropTypes.bool).isRequired,
  toggleVisibility: PropTypes.func.isRequired,
  blockName: PropTypes.string.isRequired,
  blockTitle: PropTypes.node,
};

AggregatedPricingSection.defaultProps = {
  pricingFields: null,
  readOnly: false,
  readOnlyTooltip: null,
  blockTitle: 'Pricing',
};

export default memo(AggregatedPricingSection);
